/* eslint-disable import/no-cycle */
// i18n
import './locales/i18n';

// tailwind
import './tailwind.css';

// scroll bar
import 'simplebar/src/simplebar.css';

// Mapa, editor, lightbox e carrossel importam o próprio CSS (e o mapa, o worker) no
// componente que os usa: aqui eles entravam no pacote inicial de toda página.

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';

// ----------------------------------------------------------------------

import PropTypes from 'prop-types';
import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { hotjar } from 'react-hotjar';
// @mui
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// routes
import Router from './routes';
import { queryClient } from './queryClient';
// theme
import ThemeProvider from './theme';
// locales
import ThemeLocalization from './locales';
import PickersLocalizationProvider from './locales/PickersLocalizationProvider';
// components
import { MotionLazyContainer } from './components/animate';
// Direto do arquivo: o barrel `components/chart` importa o `react-apexcharts`, e o
// apexcharts entraria no pacote inicial só para os estilos globais do gráfico.
import StyledChart from './components/chart/styles';
import GlobalErrorBoundary from './components/error-boundary';
import ScrollToTop from './components/scroll-to-top';
import { SettingsProvider, ThemeSettings } from './components/settings';
import SnackbarProvider from './components/snackbar';

// Check our docs
// https://docs.minimals.cc/authentication/js-version

import BatchVideoUploadProvider from './contexts/BatchVideoUploadContext';
// import { AuthProvider } from './auth/Auth0Context';
// import { AuthProvider } from './auth/FirebaseContext';
// import { AuthProvider } from './auth/AwsCognitoContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';

// ----------------------------------------------------------------------

// Reexportado para nao quebrar quem importa de '@/App' (ex.: contexts/SubscriptionContext.js).
export { queryClient };

const siteId = process.env.REACT_APP_HOTJAR_SITE_ID;
const hotjarVersion = process.env.REACT_APP_HOTJAR_VERSION;

export function TestProviders({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <PickersLocalizationProvider>
          <SettingsProvider>
            <BrowserRouter>
              <ScrollToTop />
              <MotionLazyContainer>
                <ThemeProvider>
                  <ThemeSettings>
                    <ThemeLocalization>
                      <SnackbarProvider>
                        <StyledChart />
                        <BatchVideoUploadProvider>
                          <ImpersonationProvider>
                            {children}
                            <ReactQueryDevtools />
                          </ImpersonationProvider>
                        </BatchVideoUploadProvider>
                      </SnackbarProvider>
                    </ThemeLocalization>
                  </ThemeSettings>
                </ThemeProvider>
              </MotionLazyContainer>
            </BrowserRouter>
          </SettingsProvider>
        </PickersLocalizationProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
const clarityProjectId = 'yqy45uqfgf';
const clarityScriptId = 'clarity-script-loader';
const clarityHostname = 'app.cakto.com.br';

// Pelo host e não pelo NODE_ENV: o build de dev1/stg também sai com NODE_ENV=production
// e mandava sessão de teste para o projeto de produção.
const shouldLoadClarity = () =>
  typeof window !== 'undefined' && window.location.hostname === clarityHostname;

export default function App() {
  useEffect(() => {
    if (!measurementId || typeof window === 'undefined') return;
    ReactGA.initialize(measurementId, {
      gtagOptions: { send_page_view: false },
    });
  }, []);

  useEffect(() => {
    if (!shouldLoadClarity() || document.getElementById(clarityScriptId)) {
      return;
    }

    const script = document.createElement('script');
    script.id = clarityScriptId;
    script.type = 'text/javascript';
    script.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${clarityProjectId}");
    `;

    document.head.appendChild(script);
  }, []);

  hotjar.initialize({
    id: siteId,
    sv: hotjarVersion
  });

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <PickersLocalizationProvider>
          <SettingsProvider>
            <BrowserRouter>
              <ScrollToTop />
              <MotionLazyContainer>
                <ThemeProvider>
                  <GlobalErrorBoundary>
                    <ThemeSettings>
                      <ThemeLocalization>
                        <SnackbarProvider>
                          <StyledChart />
                          <BatchVideoUploadProvider>
                            <Router />
                            <ReactQueryDevtools />
                          </BatchVideoUploadProvider>
                        </SnackbarProvider>
                      </ThemeLocalization>
                    </ThemeSettings>
                  </GlobalErrorBoundary>
                </ThemeProvider>
              </MotionLazyContainer>
            </BrowserRouter>
          </SettingsProvider>
        </PickersLocalizationProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

TestProviders.propTypes = {
  children: PropTypes.node
};
